import React, { ReactNode, useRef, useState } from 'react';
import GstConsentLogLink from './GstConsentLogLink';
import GstConsentLinkButton from './GstConsentLinkButton';
import { CONSENT_LINK_COPY_ID, GST_CONSENT_LINK_FAILED_TOAST, GST_CONSENT_LINK_LABEL, GST_CONSENT_LOG_LABEL } from './constants';
import ConsentAuditDrawer from '../../../../common/ConsentAudit/ConsentAuditDrawer';
import useConsentAuditDrawer from '../../../../common/ConsentAudit/useConsentAuditDrawer';
import { ConsentType } from '../../../../common/ConsentAudit/types';
import type { TabBarAction } from '../../../../common/TabBarActions/TabBarActions';
import useCopyText from '../../../../common/_custom/Hooks/useCopyText/useCopyText';
import { toast } from 'react-toastify';
import { MagicLinkCancelledError, MagicLinkResponseError, useAssesseeMagicLink } from '../../../../common/hooks/useAssesseeMagicLink';
import { useTabBarDataAvailabilityAction } from '../../components/Reconciliations/hooks/useTabBarDataAvailabilityAction';

/**
 * Builds the GST bar actions and the dialogs/drawers they open. All state lives here (not in the inline buttons)
 * so a re-fit that moves an action into ⋯ can never unmount an open dialog.
 */
export function useGstTabBarActions(orgId: string, hasGstData: boolean, isPDFReport: boolean) {
    const menuTriggerRef = useRef<HTMLButtonElement>(null);
    const [isGeneratingLink, setIsGeneratingLink] = useState(false);
    const { generate } = useAssesseeMagicLink();
    const { copyDirectText, copiedId } = useCopyText();
    const consentDrawer = useConsentAuditDrawer();
    const { action: dataAvailabilityAction, modal: dataAvailabilityModal } = useTabBarDataAvailabilityAction(
        orgId,
        isPDFReport,
        menuTriggerRef,
    );

    const copyConsentLink = async () => {
        if (isGeneratingLink) return;
        setIsGeneratingLink(true);
        try {
            await copyDirectText(await generate(orgId), CONSENT_LINK_COPY_ID);
        } catch (error) {
            // The saga toasts a responseCode ERROR itself and a superseded request is silent; only the thrown/network path needs a toast.
            if (!(error instanceof MagicLinkResponseError || error instanceof MagicLinkCancelledError))
                toast.error(GST_CONSENT_LINK_FAILED_TOAST);
        } finally {
            setIsGeneratingLink(false);
        }
    };
    // Intentionally gated on GST-returns data, not on consent records: the drawer reads org-wide ConsentAudit
    // history fetched only when it opens, and the two datasets can disagree.
    const openConsentLog = () => consentDrawer.openDrawer({ orgId, type: ConsentType.GST });

    const actions: TabBarAction[] = [
        {
            id: 'consentLink',
            inline: (
                <GstConsentLinkButton copied={copiedId === CONSENT_LINK_COPY_ID} loading={isGeneratingLink} onClick={copyConsentLink} />
            ),
            menuItem: { name: GST_CONSENT_LINK_LABEL, onClick: copyConsentLink, disabled: isGeneratingLink },
        },
        {
            id: 'consentLog',
            inline: <GstConsentLogLink disabled={!hasGstData} onOpen={openConsentLog} />,
            menuItem: { name: GST_CONSENT_LOG_LABEL, onClick: openConsentLog, disabled: !hasGstData },
        },
        ...(dataAvailabilityAction ? [dataAvailabilityAction] : []),
    ];

    const overlays: ReactNode = (
        <>
            <ConsentAuditDrawer
                open={consentDrawer.open}
                onClose={consentDrawer.closeDrawer}
                loading={consentDrawer.loading}
                loadFailed={consentDrawer.loadFailed}
                entries={consentDrawer.entries}
                orgName={consentDrawer.orgName}
                onDownload={consentDrawer.downloadAuditTrail}
                downloading={consentDrawer.downloading}
            />
            {dataAvailabilityModal}
        </>
    );

    return { actions, menuTriggerRef, overlays };
}
